import { Box, Stack, Typography } from '@mui/material';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import OutlinedPageBox from '../global/OutlinedPageBox';
import CenteredContentLayout from '../global/CenteredContentLayout';

interface Props {
  // Studio admins/managers get the shop-scoped FAQ answers.
  isManagerView: boolean;
  onOpenSettings: () => void;
}

interface FaqItem {
  question: string;
  answer: string;
}

const ARTIST_FAQ: FaqItem[] = [
  {
    question: 'How do clients join the waitlist?',
    answer:
      'From your booking page — when someone can’t find a time on your calendar (or your books are closed), they can tap “Join waitlist” and pick the dates, days of the week, and times that work for them.',
  },
  {
    question: 'How does Porter know when to message people?',
    answer:
      'Whenever an appointment is canceled or rescheduled, Porter checks the opening against each member’s preferences and your real availability, then automatically texts and emails everyone who matches. Your notification style setting controls whether everyone hears at once or a few at a time.',
  },
];

const MANAGER_FAQ: FaqItem[] = [
  {
    question: 'Can I change waitlist settings for an artist?',
    answer:
      'Artists manage their own waitlist settings from the Porter app. To set them shop-wide, go to Booking Settings → Default Artist Settings — you can apply changes to all current artists at once.',
  },
  {
    question: 'Can I have a shop-wide waitlist?',
    answer:
      'Waitlists are per artist, so clients always wait for a specific artist’s openings. Turning the waitlist on for every artist via Default Artist Settings gives you the shop-wide equivalent, and this page shows every artist’s waitlist in one place.',
  },
];

function WaitlistEmptyState({ isManagerView, onOpenSettings }: Props) {
  const faq = isManagerView ? [...ARTIST_FAQ, ...MANAGER_FAQ] : ARTIST_FAQ;

  return (
    <OutlinedPageBox>
      <CenteredContentLayout
        image={{
          src: '/cancellation-waitlist.svg',
          alt: 'Cancellation waitlist',
        }}
        title="No one is on the waitlist yet"
        description="Clients can join your waitlist from your booking page when they can't find a time. When a cancellation opens up a matching slot, Porter notifies them automatically so it gets rebooked fast."
        secondaryButton={{
          icon: SettingsOutlinedIcon,
          text: 'Waitlist Settings',
          onClick: onOpenSettings,
        }}
        maxWidth="750px"
        imageMaxWidth="120px">
        <Stack spacing={3} width="100%">
          {faq.map(item => (
            <Box key={item.question}>
              <Typography variant="h5" gutterBottom>
                {item.question}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {item.answer}
              </Typography>
            </Box>
          ))}
        </Stack>
      </CenteredContentLayout>
    </OutlinedPageBox>
  );
}

export default WaitlistEmptyState;
